// ─────────────────────────────────────────────────────────────────────
// accountAccess.js — the account-status ladder + free-override helpers.
//
// Single source of truth for "what screen does this account get" on the
// web app. Mirrors the DB predicates (has_active_access /
// account_access_status, migration 029): a free override — either the
// boolean flag or the legacy subscription_status='free_override' encoding —
// grants access only while free_override_expires_at is NULL or in the
// future. When it expires the account falls through to its OWN standing,
// and only lands on 'override_expired' (the trial-end-style paywall) when
// nothing else applies.
//
// Pure functions (account row in, string out) so the ladder is unit-testable
// — the useAccountStatus hook in App.js is just fetch + this.
// ─────────────────────────────────────────────────────────────────────

export function overrideFlagged(account) {
  return !!account && (account.free_override === true || account.subscription_status === "free_override");
}

// Override grants access: flag present AND (no expiry OR expiry in the future).
export function isOverrideActive(account, now = new Date()) {
  if (!overrideFlagged(account)) return false;
  const exp = account.free_override_expires_at ? new Date(account.free_override_expires_at) : null;
  return !exp || exp > now;
}

// Flag still present but the expiry has passed — the paywall state.
// Defined as the exact complement of isOverrideActive so the two predicates
// can never drift apart.
export function isOverrideExpired(account, now = new Date()) {
  return overrideFlagged(account) && !isOverrideActive(account, now);
}

// Comped without an own paid subscription — the population the one-workspace
// cap (and the free-access countdown banner) applies to.
export function isOverrideCapped(account, now = new Date()) {
  return isOverrideActive(account, now) && account.subscription_status !== "active";
}

// The status ladder. Returns one of:
//   active | pre_trial | trial_expired | override_expired | past_due |
//   archived | canceled_expired | inactive | no_account
export function computeAccountStatus(acc, now = new Date()) {
  if (!acc) return "no_account";
  const trialEnd = acc.trial_ends_at ? new Date(acc.trial_ends_at) : null;
  const periodEnd = acc.current_period_end ? new Date(acc.current_period_end) : null;
  const retentionEnd = acc.data_retention_until ? new Date(acc.data_retention_until) : null;
  const overrideActive = isOverrideActive(acc, now);
  const overrideExpired = isOverrideExpired(acc, now);

  if (overrideActive) return "active";
  if (acc.subscription_status === "active") return "active";
  // Pre-trial (no trial clock yet) renders the normal app — but an EXPIRED
  // override must not fall back into that open-ended free ride (matches the
  // has_active_access guard in migration 029).
  if (acc.subscription_status === "trialing" && trialEnd === null) {
    return overrideExpired ? "override_expired" : "pre_trial";
  }
  if (acc.subscription_status === "trialing" && trialEnd > now) return "active";
  // Trial appears expired BUT user has a Stripe subscription — they paid,
  // card is on file, Stripe will auto-charge any moment and the webhook flips
  // the status. For ordinary accounts this stays UNBOUNDED (never show a
  // subscribe-again paywall to someone who already paid — a second checkout
  // would double-charge; RLS separately bounds writes to 1h server-side).
  // Only when an EXPIRED override is present is it bounded to the same
  // 1-hour window as has_active_access — otherwise a stale dead
  // subscription id would mask the expiry paywall forever.
  if (acc.subscription_status === "trialing" && acc.stripe_subscription_id
      && (!overrideExpired || trialEnd > new Date(now.getTime() - 60 * 60 * 1000))) {
    return "active";
  }
  // True trial-expired: trialing, date passed, no (live-enough) subscription.
  if (acc.subscription_status === "trialing") {
    return overrideExpired ? "override_expired" : "trial_expired";
  }
  if (acc.subscription_status === "past_due") return "past_due";
  if (acc.subscription_status === "canceled" && periodEnd && periodEnd > now) return "active";
  if (acc.archived_at && (!retentionEnd || retentionEnd > now)) return "archived";
  if (acc.archived_at) return "canceled_expired";
  return overrideExpired ? "override_expired" : "inactive";
}
